import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ChevronDown, ChevronUp, HelpCircle } from "lucide-react";
import { entRunSimulator } from "../salaryEnterpriseApi";
import { getTemplates } from "../../SalaryApi";
import { useToast } from "../../../Context/ToastContext";
import DatePickerField from "../../../../components/common/DatePickerField";

export default function SalarySimulatorAdvanced() {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [showGuide, setShowGuide] = useState(true);
  const [form, setForm] = useState({
    template_id: "",
    ctc: 1200000,
    as_of_date: new Date().toISOString().slice(0, 10),
    employee_id: "",
    tax_regime: "new",
    location_state: "KA",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getTemplates()
      .then((rows) => setTemplates(Array.isArray(rows) ? rows : []))
      .catch(() => setTemplates([]))
      .finally(() => setLoadingTemplates(false));
  }, []);

  const selectedTemplate = templates.find((t) => String(t.template_id) === String(form.template_id));

  async function run() {
    if (!form.template_id) {
      toast.error("Select a salary template first");
      return;
    }
    try {
      setLoading(true);
      const res = await entRunSimulator({
        template_id: form.template_id,
        ctc: Number(form.ctc),
        as_of_date: form.as_of_date,
        employee_id: form.employee_id || null,
        tax_regime: form.tax_regime,
        location_state: form.location_state || null,
        label: "UI simulator",
      });
      setResult(res);
      toast.success("Simulation complete");
    } catch (e) {
      toast.error(e?.detail || "Simulation failed");
    } finally {
      setLoading(false);
    }
  }

  const summary = result?.summary || {};
  const projections = result?.projections || {};

  return (
    <div className="bw-stack">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="bw-section-title">Payroll calculator</h2>
          <p className="bw-section-hint">
            Preview monthly gross, statutory deductions, TDS, net pay, and employer cost — without running payroll.
          </p>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-900 font-medium"
          onClick={() => setShowGuide((v) => !v)}
        >
          <BookOpen size={16} />
          {showGuide ? "Hide guide" : "Show guide"}
          {showGuide ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {showGuide ? (
        <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-4 md:p-5 space-y-4 text-sm text-slate-700">
          <div className="flex items-start gap-2">
            <HelpCircle size={18} className="text-teal-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-slate-900">How to use the simulator</p>
              <p className="mt-1 text-slate-600 leading-relaxed">
                This tool answers: &quot;If I pay this CTC using this salary template, what will gross, PF, TDS, and net look like?&quot;
                Results are a <strong>preview only</strong> — they do not create payroll runs or payslips.
              </p>
            </div>
          </div>

          <ol className="list-decimal pl-5 space-y-2 leading-relaxed">
            <li>
              <strong>Pick a salary template</strong> from the dropdown below (e.g. Developer Pack, MARKET PACK).
              If the list is empty, create one first under{" "}
              <Link to="/salary/templates" className="text-teal-700 underline font-medium">
                Payroll → Templates
              </Link>
              , add components (Basic, HRA, PF, etc.), then return here.
            </li>
            <li>
              <strong>Enter annual CTC</strong> in rupees — e.g. <code className="bg-white/80 px-1 rounded">1200000</code> for ₹12 LPA.
            </li>
            <li>
              <strong>As of date</strong> — use the payroll month you want to simulate (affects statutory rules and formulas).
            </li>
            <li>
              <strong>Tax regime</strong> — New or Old — used for a simplified TDS estimate in the simulator.
            </li>
            <li>
              <strong>State (PT)</strong> — 2-letter state code for Professional Tax, e.g. <code className="bg-white/80 px-1 rounded">KA</code> (Karnataka),{" "}
              <code className="bg-white/80 px-1 rounded">MH</code> (Maharashtra).
            </li>
            <li>
              Click <strong>Run simulation</strong> and review the summary cards (gross, net, TDS, PF, ESI, PT, employer cost).
            </li>
          </ol>

          <div className="rounded-lg bg-white/80 border border-teal-100 p-3 text-xs text-slate-600 space-y-1">
            <p className="font-semibold text-slate-800">Where does the template ID come from?</p>
            <p>
              Each salary template has a unique ID (UUID). You no longer need to copy it manually — choose the template by name in the dropdown.
              The ID is shown below the dropdown for reference if you need it for API or support.
            </p>
            <p>
              You can also find templates under{" "}
              <Link to="/employeesalarystructure" className="text-teal-700 underline">
                Assign Salary Package
              </Link>{" "}
              or{" "}
              <Link to="/salary/templates" className="text-teal-700 underline">
                Salary Templates
              </Link>
              .
            </p>
          </div>
        </div>
      ) : null}

      <div className="bw-form-grid">
        <div className="bw-field xl-8">
          <label className="bw-label">Salary template *</label>
          {loadingTemplates ? (
            <p className="text-sm text-slate-500 py-2">Loading templates…</p>
          ) : templates.length === 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              No templates found.{" "}
              <Link to="/salary/templates" className="underline font-medium">
                Create a salary template first
              </Link>
            </div>
          ) : (
            <select
              className="bw-select"
              value={form.template_id}
              onChange={(e) => setForm((p) => ({ ...p, template_id: e.target.value }))}
            >
              <option value="">Select salary template</option>
              {templates.map((t) => (
                <option key={t.template_id} value={t.template_id}>
                  {t.name}
                  {t.description ? ` — ${t.description}` : ""}
                </option>
              ))}
            </select>
          )}
          {selectedTemplate ? (
            <p className="mt-1 text-[11px] text-slate-400 font-mono break-all">
              Template ID: {selectedTemplate.template_id}
            </p>
          ) : null}
        </div>
        <div className="bw-field xl-4">
          <label className="bw-label">CTC (annual ₹)</label>
          <input
            type="number"
            min="0"
            className="bw-input"
            value={form.ctc}
            onChange={(e) => setForm((p) => ({ ...p, ctc: e.target.value }))}
            placeholder="e.g. 1200000"
          />
          <p className="mt-1 text-[11px] text-slate-500">
            Monthly ≈ ₹{Number(form.ctc) > 0 ? Math.round(Number(form.ctc) / 12).toLocaleString("en-IN") : "—"}
          </p>
        </div>
        <div className="bw-field xl-4">
          <label className="bw-label">As of date</label>
          <DatePickerField
            hideLabel
            value={form.as_of_date}
            onChange={(date) => setForm((p) => ({ ...p, as_of_date: date }))}
            inputClassName="bw-input"
          />
        </div>
        <div className="bw-field xl-4">
          <label className="bw-label">Tax regime</label>
          <select
            className="bw-select"
            value={form.tax_regime}
            onChange={(e) => setForm((p) => ({ ...p, tax_regime: e.target.value }))}
          >
            <option value="new">New regime</option>
            <option value="old">Old regime</option>
          </select>
        </div>
        <div className="bw-field xl-4">
          <label className="bw-label">State (Professional Tax)</label>
          <input
            className="bw-input"
            value={form.location_state}
            onChange={(e) => setForm((p) => ({ ...p, location_state: e.target.value.toUpperCase() }))}
            placeholder="KA"
            maxLength={3}
          />
          <p className="mt-1 text-[11px] text-slate-500">2-letter state code for PT calculation</p>
        </div>
      </div>

      <button
        type="button"
        className="bw-btn bw-btn-primary"
        disabled={loading || !form.template_id}
        onClick={run}
      >
        {loading ? "Running…" : "Run simulation"}
      </button>

      {result ? (
        <div className="space-y-4">
          <p className="text-sm font-semibold text-slate-800">Simulation results (monthly unless noted)</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["Gross (mo)", summary.gross_monthly],
              ["Net (mo)", summary.net_salary],
              ["TDS (mo)", summary.tds_monthly],
              ["PF (employee)", summary.pf_employee],
              ["ESI (employee)", summary.esi_employee],
              ["Professional Tax", summary.pt],
              ["Employer cost (mo)", summary.employer_cost_monthly],
              ["Annual net", projections.annual_net],
            ].map(([label, val]) => (
              <div key={label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs text-slate-500">{label}</p>
                <p className="text-lg font-semibold tabular-nums">
                  ₹{Number(val || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
